/**
 * Cabinet → My Deliveries (Phase E)
 *
 * Lists the customer's upcoming + recent deliveries. Allows confirming
 * receipt of a delivery the admin has already marked delivered.
 */
import React, { useEffect, useState } from 'react';
import { Truck, CheckCircle2, Loader2, Package } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/cabinet/lib/api';
import { useI18n } from '@/cabinet/i18n';
import PageHeader from '@/cabinet/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

function fmtDate(iso, lang) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === 'ka' ? 'ka-GE' : 'en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch (_) { return iso; }
}

export default function DeliveriesPage() {
  const { t, lang } = useI18n();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState({});

  useEffect(() => { document.title = 'SmartPaw — Deliveries'; }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/cabinet/deliveries');
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const confirmReceived = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.post(`/cabinet/deliveries/${id}/confirm`);
      toast.success(t('deliveries.confirmed_toast'));
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const upcoming = items.filter((d) => !['delivered', 'cancelled'].includes(d.status));
  const delivered = items.filter((d) => d.status === 'delivered');

  return (
    <div data-testid="deliveries-page">
      <PageHeader title={t('deliveries.title')} subtitle={t('deliveries.subtitle')} testIdPrefix="deliveries" />

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
        </div>
      ) : items.length === 0 ? (
        <Card className="border-[hsl(var(--border))] bg-paper" data-testid="deliveries-empty">
          <CardContent className="py-10 text-center">
            <Package className="h-8 w-8 mx-auto text-[hsl(var(--muted-foreground))] mb-2" />
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('deliveries.empty')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <Section title={t('deliveries.upcoming_title')} items={upcoming}>
            {(d) => (
              <DeliveryRow
                key={d.id}
                d={d}
                lang={lang}
                t={t}
                action={d.customer_confirmed_at ? (
                  <Badge className="rounded-full bg-emerald-100 text-emerald-800 text-[10px]" data-testid={`confirmed-badge-${d.id}`}>
                    ✓ {t('deliveries.confirmed_badge')}
                  </Badge>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => confirmReceived(d.id)}
                    disabled={!!busy[d.id]}
                    className="h-9 rounded-full bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-white"
                    data-testid={`confirm-delivery-${d.id}`}
                  >
                    {busy[d.id] ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1.5" />}
                    {t('deliveries.confirm_reminder')}
                  </Button>
                )}
              />
            )}
          </Section>

          <Section title={t('deliveries.history_title')} items={delivered}>
            {(d) => (
              <DeliveryRow
                key={d.id}
                d={d}
                lang={lang}
                t={t}
                action={(
                  <Badge className="rounded-full bg-neutral-800 text-white text-[10px]">
                    {t('deliveries.delivered_badge')}
                  </Badge>
                )}
              />
            )}
          </Section>
        </div>
      )}
    </div>
  );
}

function Section({ title, items, children }) {
  if (items.length === 0) return null;
  return (
    <div>
      <h2 className="font-serif text-lg font-semibold mb-3 text-[hsl(var(--foreground))]">{title}</h2>
      <div className="space-y-2">
        {items.map(children)}
      </div>
    </div>
  );
}

function DeliveryRow({ d, lang, t, action }) {
  return (
    <Card className="border-[hsl(var(--border))] bg-paper" data-testid={`delivery-row-${d.id}`}>
      <CardContent className="flex items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="rounded-full bg-[hsl(var(--secondary))] p-2 text-[hsl(var(--primary))]">
            <Truck className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm text-[hsl(var(--foreground))] truncate">{d.product_name}<span className="font-normal text-xs text-[hsl(var(--muted-foreground))]"> × {d.qty}</span></div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">{fmtDate(d.scheduled_at, lang)} · {d.subscription_label}{d.payment_status === 'paid' ? ` · ${t('deliveries.paid')}` : ''}</div>
          </div>
        </div>
        <div className="shrink-0">{action}</div>
      </CardContent>
    </Card>
  );
}
